import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { IBidResponse } from 'src/app/article/interfaces/bid-response.interface';
import {
  AuctionPrice,
  BidDto,
  UserAutoRebid,
} from 'src/app/articles-manager/interfaces/auction.interface';
import {
  AuctionPriceInfo,
  DonationDto,
  EventNotification,
  MemoraEndDate,
  MemoraEventAuction,
  MemoraEventAuctionView,
  MemoraEventSaveDto,
  MemoraEventSummary,
  MemoraEventView,
  NotificationStatusDto,
} from '../interfaces/event.interface';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private eventViewSubject = new BehaviorSubject<{
    [lang: number]: {
      [key: string]: MemoraEventAuctionView;
    };
  }>({});

  private eventAmountSubject = new BehaviorSubject<{
    [idEvent: number]: number;
  }>({});

  private filters: {
    onlyFavorites: boolean;
    searchText: string;
    orderOption: number;
    idCategory: number[];
  } = {
    onlyFavorites: false,
    searchText: '',
    orderOption: -1,
    idCategory: [],
  };

  constructor(private _api: ApiService) {}

  public get filtersValue() {
    return this.filters;
  }

  public updateFilters(
    filters: Partial<{
      onlyFavorites: boolean;
      searchText: string;
      orderOption: number;
      idCategory: number[];
    }>
  ) {
    this.filters = { ...this.filters, ...filters };
  }

  public resetFilters() {
    this.filters = {
      onlyFavorites: false,
      searchText: '',
      orderOption: 0,
      idCategory: [],
    };
  }

  getAll(): Observable<MemoraEventView[]> {
    return this._api.request(ApiServiceHolder.memorabidevent.getAll());
  }

  getByPk(idEvent: number): Observable<MemoraEventAuction> {
    return this._api.request(ApiServiceHolder.memorabidevent.getByPk(idEvent));
  }

  getBySlug(
    slug: string,
    loadOnlyVisible: boolean
  ): Observable<MemoraEventAuction> {
    return this._api.request(
      ApiServiceHolder.memorabidevent.getBySlug(slug, loadOnlyVisible)
    );
  }

  getAllViewByPk(
    idEvent: number,
    idLanguage: number
  ): Observable<MemoraEventAuctionView> {
    return this._api.request(
      ApiServiceHolder.memorabidevent.getAllViewByPk(idEvent, idLanguage)
    );
  }

  getAllViewBySlug(
    slug: string,
    idlanguage: number
  ): Observable<MemoraEventAuctionView | null> {
    this._api
      .request(
        ApiServiceHolder.memorabidevent.getAllViewBySlug(slug, idlanguage)
      )
      .subscribe((event) => {
        const events = this.eventViewSubject.value;

        this.eventViewSubject.next({
          ...events,
          [idlanguage]: {
            ...events[idlanguage],
            [slug]: event as MemoraEventAuctionView,
          },
        });
      });

    return this.eventViewSubject
      .asObservable()
      .pipe(
        switchMap((events) =>
          of((events[idlanguage]?.[slug] as MemoraEventAuctionView) ?? null)
        )
      );

    // .pipe((events) => events[idlanguage]?.[slug] ?? {});
  }

  updateCurrentPrices(idLanguage: number, slug: string) {
    const events = this.eventViewSubject.value;
    const auctionList = events[idLanguage][slug]?.auctionList ?? [];

    if (auctionList.length === 0) {
      return of([]);
    }

    return this._api
      .request<AuctionPrice[]>(
        ApiServiceHolder.auction.getCurrentPrices(
          auctionList.map((auction) => auction.idAuction)
        )
      )
      .pipe(
        switchMap((prices) => {
          const events = this.eventViewSubject.value;
          const pricesMap = prices.reduce((acc, price) => {
            acc[price.idAuction] = price;
            return acc;
          }, {} as { [idAuction: number]: AuctionPrice });

          const priceChange = events[idLanguage][slug].auctionList.some(
            (auction) => {
              const price = pricesMap[auction.idAuction];
              return (
                price?.currentMaxOfferedPrice !==
                  auction.currentMaxOfferedPrice ||
                price?.hasUserBided !== auction.hasUserBided ||
                price?.isUserBestBider !== auction.isUserBestBider
              );
            }
          );

          if (!priceChange) {
            return of(prices);
          }

          const auctionList = events[idLanguage][slug].auctionList.map(
            (auction) => {
              const price = pricesMap[auction.idAuction];
              return {
                ...auction,
                hasUserBided: price?.hasUserBided ?? auction.hasUserBided,
                isUserBestBider:
                  price?.isUserBestBider ?? auction.isUserBestBider,
                userMaxOfferedPrice:
                  price?.userMaxOfferedPrice ?? auction.userMaxOfferedPrice,
                reservePriceReached:
                  price?.reservePriceReached ?? auction.reservePriceReached,
                currentMaxOfferedPrice:
                  price?.currentMaxOfferedPrice ??
                  auction.currentMaxOfferedPrice,
              };
            }
          );

          this.eventViewSubject.next({
            ...events,
            [idLanguage]: {
              ...events[idLanguage],
              [slug]: {
                ...events[idLanguage][slug],
                auctionList,
              },
            },
          });

          return of(prices);
        })
      );
  }

  saveEvent(memoraEventSaveDto: MemoraEventSaveDto) {
    return this._api.request(
      ApiServiceHolder.memorabidevent.save(memoraEventSaveDto)
    );
  }

  saveDonation(donation: DonationDto) {
    return this._api.request(ApiServiceHolder.donation.save(donation));
  }

  getSummary(
    idEvent: number,
    idLanguage: number
  ): Observable<MemoraEventSummary[]> {
    return this._api.request(
      ApiServiceHolder.memorabidevent.getSummary(idEvent, idLanguage)
    );
  }

  getEventNotification(
    idEvent: number,
    idLanguage: number
  ): Observable<EventNotification[]> {
    return this._api.request(
      ApiServiceHolder.memorabidevent.getEventNotification(idEvent, idLanguage)
    );
  }

  getTotalAmount(idEvent: number): Observable<number> {
    this._api
      .request(ApiServiceHolder.memorabidevent.getTotalAmount(idEvent))
      .subscribe((amount) => {
        const amounts = this.eventAmountSubject.value;
        this.eventAmountSubject.next({
          ...amounts,
          [idEvent]: amount as number,
        });
      });

    return this.eventAmountSubject
      .asObservable()
      .pipe(switchMap((amounts) => of(amounts[idEvent] ?? 0)));
  }

  getEventDateEnd(idEvent: number): Observable<MemoraEndDate> {
    return this._api.request(
      ApiServiceHolder.memorabidevent.getEventDateEnd(idEvent)
    );
  }

  bid(bid: BidDto): Observable<IBidResponse> {
    return this._api.request(ApiServiceHolder.memorabidevent.bid(bid));
  }

  autobid(bid: UserAutoRebid): Observable<UserAutoRebid> {
    return this._api.request(ApiServiceHolder.memorabidevent.autoRebid(bid));
  }

  getAuctionPriceInfo(idAuction: number): Observable<AuctionPriceInfo> {
    return this._api.request(
      ApiServiceHolder.auction.getAuctionPriceInfo(idAuction)
    );
  }

  setNotificationStatus(status: NotificationStatusDto) {
    return this._api.request(
      ApiServiceHolder.memorabidevent.setNotificationStatus(status)
    );
  }
}
